import { renderSonicTraits, type SonicTraits } from "../../domain/index.js";
import { Badge } from "../../ui/Badge.js";

// Display labels for the chip rows. Kept in sync with SonicTraitsEditor; the
// vocabulary is owned by `@studio/domain`, these are pt-BR display strings.
const TERM_LABELS: Record<string, string> = {
  // genres
  rock: "rock",
  metal: "metal",
  pop: "pop",
  electronic: "eletrônico",
  hiphop: "hip-hop",
  jazz: "jazz",
  funk: "funk",
  folk: "folk",
  classical: "clássico",
  rnb: "r&b",
  reggae: "reggae",
  ambient: "ambient",
  // eras
  "60s": "anos 60",
  "70s": "anos 70",
  "80s": "anos 80",
  "90s": "anos 90",
  "2000s": "anos 2000",
  modern: "moderno",
  // timbres
  warm: "quente",
  bright: "brilhante",
  dark: "escuro",
  gritty: "áspero",
  clean: "limpo",
  lofi: "lo-fi",
  lush: "denso",
  minimal: "minimal",
  // vibes
  melancholic: "melancólico",
  uplifting: "animado",
  aggressive: "agressivo",
  dreamy: "onírico",
  playful: "brincalhão",
  epic: "épico",
  nostalgic: "nostálgico",
  groovy: "groovy",
};

const ENERGY_LABELS: Record<number, string> = {
  1: "muito calmo",
  2: "relaxado",
  3: "neutro",
  4: "energético",
  5: "intenso",
};

function label(term: string): string {
  return TERM_LABELS[term] ?? term;
}

function isEmpty(t: SonicTraits): boolean {
  return (
    (t.genres ?? []).length === 0 &&
    t.era === undefined &&
    t.energy === undefined &&
    (t.timbre ?? []).length === 0 &&
    (t.vibe ?? []).length === 0
  );
}

interface Props {
  traits: SonicTraits;
  /** Optional message shown when the traits are empty. */
  emptyHint?: string;
}

/**
 * Read-only render of a `SonicTraits` set — chip rows per axis plus the
 * rendered prompt snippet. Used by the band "Som da banda" card and the
 * song-editor "herdar da banda" mode.
 */
export function SonicTraitsCard({ traits, emptyHint }: Props): JSX.Element {
  if (isEmpty(traits)) {
    return (
      <p className="font-mono text-xs italic leading-relaxed text-ink-dim">
        {emptyHint ?? "Nenhum traço sonoro — a geração fica neutra."}
      </p>
    );
  }

  const snippet = renderSonicTraits(traits);

  return (
    <div className="flex flex-col gap-3">
      <dl className="flex flex-col gap-2">
        {(traits.genres ?? []).length > 0 && (
          <ChipRow label="gêneros" terms={traits.genres ?? []} />
        )}
        {traits.era !== undefined && <ChipRow label="época" terms={[traits.era]} />}
        {traits.energy !== undefined && (
          <ChipRow
            label="energia"
            terms={[`${String(traits.energy)} · ${ENERGY_LABELS[traits.energy] ?? ""}`]}
            raw
          />
        )}
        {(traits.timbre ?? []).length > 0 && <ChipRow label="timbre" terms={traits.timbre ?? []} />}
        {(traits.vibe ?? []).length > 0 && <ChipRow label="vibe" terms={traits.vibe ?? []} />}
      </dl>

      {snippet && (
        <p className="border-l-2 border-oscillo bg-asphalt/60 px-3 py-2 font-mono text-[11px] leading-relaxed text-ink-muted">
          {snippet}
        </p>
      )}
    </div>
  );
}

function ChipRow({
  label: rowLabel,
  terms,
  raw = false,
}: {
  label: string;
  terms: readonly string[];
  raw?: boolean;
}): JSX.Element {
  return (
    <div className="grid grid-cols-[64px_1fr] items-start gap-3">
      <dt className="pt-1 font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">
        {rowLabel}
      </dt>
      <dd className="flex flex-wrap gap-1.5">
        {terms.map((t) => (
          <Badge key={t} tone="active">
            {raw ? t : label(t)}
          </Badge>
        ))}
      </dd>
    </div>
  );
}
